import { takeEvery, all, select, put } from 'redux-saga/effects';
import firestoreRedux from '@dreamworld/firestore-redux';
import * as auth from '../auth';
import * as multipleLanguage from '../multiple-language';

/** Id of the one Translation activation that covers the whole impersonated session. */
const IMPERSONATE_TRANSLATION_ID = 'impersonate';

/** Language a salesman gets the first time they impersonate on a device. */
const DEFAULT_IMPERSONATE_LANGUAGE = 'en';

/**
 * Collections never sent for translation - either they carry no user-authored prose at all
 * (metadata, ids, timestamps, enums), or they carry real secrets/PII (join links, cloud-store
 * emails, device push tokens) that must never leave the app to a third-party translate call.
 *
 * `text-resources-revision` holds the app's own static text-resource manifest - filenames and
 * revision hashes, not customer content. Static text is localized by i18next, never by the
 * translation API.
 */
const UNTRANSLATED_COLLECTIONS = [
  'text-resources-revision',
  'cloud-files',
  'tag-colors',
  'favorite-boards',
  'device-ui',
  'app',
  'blacklist-domains',
  'account-settings',
  'ai-quick-actions',
  'view',
  'view-data',
  'view-data-summary',
  'view-settings',
  'kerika-drives',
  'malicious-urls',
  'non-profit-domains',
  'offline-domains',
  'public-domains',
  'account-cloud-stores',
  'account-invitation-quota',
  'account-last-activity',
  'account-team-members',
  'account-user-preferences',
  'subscription-history-items',
  'subscriptions',
  // Every field is a language/country code, e.g. `system-language.baseLang`, `system-language.langs[].code`,
  // `country-wise-lang.countryWiseLang`. Translating a language code corrupts the very lookups
  // (`isSupportedLanguage`, `preferredLanguage`) this feature's own language switch depends on.
  'system-language',
  'country-wise-lang',
  // Pure per-user preference/UI state, no prose.
  'user-preferences',
  'user-properties',
  'user-ui',
  'user-property-definitions',
  'user-account-ui',
  'user-board-ui',
  'board-settings',
  // Pure "who has read/seen what" tracking, no prose.
  'card-unread-user-details',
  'board-attachment-unread-user-details',
  'card-attachment-unread-user-details',
  'board-chat-unread-user-details',
  'card-chat-unread-user-details',
  'column-unread-user-details',
  'task-unread-user-details',
  'board-unread-user-details',
  'board-visitor-notification-preferences',
  'lastseen-details', // also holds `ip` - PII.
  'devices', // `fcm.token` is a push-notification credential.
  // Account/board membership and request metadata, no prose.
  'account-alerts',
  'beta-users',
  'board-team-members',
  'board-move-requests',
  'import-boards-request',
  'cloud-store-access-restricted',
  'known-features',
  'non-profit-requests',
  'messages', // system notification records ({type, boardId, userId, data: {invitedBy, ...}}) - no prose field.
  // Genuine secrets - never sent to a third-party call under any circumstances.
  'board-join-links', // `.secret` is a real secret token.
  'cloud-store-emails', // every field is a literal array of real email addresses.
  'user-cloud-stores', // has a literal `email` field.
  'time-tracker-account-settings', // {provider, status, preferences} - metadata only, no prose.
  'time-tracker-account-user-settings', // `apiToken` is an encrypted secret.
  // Denormalized summaries/derived view state - every field is an id, count, boolean, timestamp, or enum.
  'cards-summary',
  'columns-summary',
  'board-switcher',
  'hidden-columns',
  'views-hidden-columns',
  'views-last-active-columns',
  // No prose of its own - real content lives on the `ai-messages` it groups.
  'ai-chats',
  // `title`/`description` are stored as per-language maps ({en: '...', hi: '...'}), not plain
  // strings - the library only translates string leaves, so this is a no-op either way. Excluded
  // for clarity and to avoid the library scanning it at all.
  'broadcast-messages',
];

let impersonateTranslationStarted = false;

/**
 * Translates the customer's own content into the salesman's language for as long as an impersonated
 * ("View As") session is running.
 *
 * One activation covers AI Helper, the board, a card's detail and Home at once - every surface's
 * Firestore reads return translated content automatically, so no component changes are needed.
 * See wiki/features/impersonate-translate/impersonate-translate.md.
 */
export function* startImpersonateTranslationSaga() {
  const state = yield select();
  if (impersonateTranslationStarted || !auth.selectors.impersonatedUser(state)) {
    return;
  }

  impersonateTranslationStarted = true;
  const language = multipleLanguage.selectors.impersonateLang(state) || DEFAULT_IMPERSONATE_LANGUAGE;

  firestoreRedux.translation.setLanguage(language);
  firestoreRedux.translation.start({
    id: IMPERSONATE_TRANSLATION_ID,
    filterFunction: (doc, collection) => !UNTRANSLATED_COLLECTIONS.includes(collection),
  });

  // Captions are localized by i18next, never by the translation API - but i18next takes its language
  // from `currentLanguage` when it initializes, which happens before `auth.user` exists and therefore
  // before this session is known to be impersonated. It would otherwise stay on whatever the device
  // was already using. Driving the change through the app's own language action switches captions and
  // keeps the salesman's choice, using the one path the profile dialog uses.
  yield put(multipleLanguage.actions.changeLanguage(language));
}

/**
 * Stops the impersonated session's Translation activation and resets internal state so a later
 * impersonation session can start a clean one.
 *
 * The salesman's translated view ends with their session: this removes every document's
 * `docs`/`status` entry, so reads fall straight back to the customer's original text.
 */
export function stopImpersonateTranslation() {
  firestoreRedux.translation.stop(IMPERSONATE_TRANSLATION_ID);
  impersonateTranslationStarted = false;
}

/**
 * Root saga - watches `auth`'s `UPDATE_AUTH` action so this module can start translation as soon
 * as impersonation begins, fully decoupled from `auth`'s own saga.
 */
export default function* impersonateTranslateSaga() {
  yield all([takeEvery(auth.actions.UPDATE_AUTH, startImpersonateTranslationSaga)]);
}
